import "./TimeInputPopup.scss";

import { Button } from "@mantine/core";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTheme } from "src/hooks";
import { useDispatch, useSelector } from "src/redux/hooks";
import { setEditing } from "src/redux/slices/entrySlice";
import { setTimeInputPopup } from "src/redux/slices/generatorFeaturesSlice";

import TimeInputPopupButton from "./TimeInputPopupButton";

const parseTimeFromValue = (value: string | undefined) => {
  if (!value || value.includes("null")) return [null, null];
  const [hour, minutes] = value.split(":");
  return [hour, minutes];
};

const TimeInputPopup = () => {
  const theme = useTheme() as any;
  const dispatch = useDispatch();
  const { timeInputPopup } = useSelector(state => state.generatorFeaturesSlice);
  const [isOpen, setIsOpen] = useState(false);
  const [hour, setHour] = useState<string | null>(null);
  const [minute, setMinute] = useState<string | null>(null);
  const hourListRef = useRef<HTMLDivElement>(null);
  const minuteListRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (timeInputPopup) {
      const [hourValue, minuteValue] = parseTimeFromValue(timeInputPopup.value);
      setHour(hourValue);
      setMinute(minuteValue);
      setIsOpen(true);

      if (hourListRef.current && hourValue)
        hourListRef.current?.scrollTo(0, (parseInt(hourValue) || 0) * 19);

      if (minuteListRef.current && minuteValue)
        minuteListRef.current?.scrollTo(0, (parseInt(minuteValue) || 0) * 19);
    }
  }, [timeInputPopup]);

  const handleChange = useCallback(
    (overwriteH?: string, overwriteM?: string) => {
      if (!timeInputPopup) return;
      if (!overwriteH && !overwriteM && (!hour || !minute)) return;

      const h = overwriteH || hour;
      const m = overwriteM || minute;

      const { minTime, maxTime, value } = timeInputPopup;
      const [hourValue, minuteValue] = parseTimeFromValue(value);
      if (hourValue === h && minuteValue === m) return;
      const timeOnlyValue = `${h}:${m}`;
      let newTimeString = timeOnlyValue;

      if (minTime && timeOnlyValue < minTime) {
        newTimeString = minTime;
      } else if (maxTime && timeOnlyValue > maxTime) {
        newTimeString = maxTime;
      }
      timeInputPopup?.onChange(newTimeString);
      dispatch(setTimeInputPopup(null));
      // dispatch(setTriggerTimeCheck(Math.random()));
      dispatch(setEditing(true));
    },
    [timeInputPopup, hour, minute, dispatch],
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;

      if (!target?.closest(".time_popup")) {
        handleChange();
        dispatch(setTimeInputPopup(null));
      }
    };

    const handleScroll = () => {
      setIsOpen(false);
      setTimeout(() => {
        handleChange();
      }, 10);
    };

    document.addEventListener("mousedown", handleClickOutside);
    const pageContainer = document.getElementById("page-container");
    pageContainer?.addEventListener("scroll", handleScroll);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      pageContainer?.removeEventListener("scroll", handleScroll);
    };
  }, [dispatch, handleChange]);
  const maxH = useMemo(
    () =>
      parseInt(
        parseTimeFromValue(timeInputPopup?.maxTime ?? "23:00")[0] ?? "0",
      ),
    [timeInputPopup?.maxTime],
  );

  const minH = useMemo(
    () =>
      parseInt(
        parseTimeFromValue(timeInputPopup?.minTime ?? "00:00")[0] ?? "0",
      ),
    [timeInputPopup?.minTime],
  );
  const handleNowClick = () => {
    const now = new Date();
    handleChange(
      now.getHours().toString().padStart(2, "0"),
      now.getMinutes().toString().padStart(2, "0"),
    );
  };

  if (!timeInputPopup) return null;

  return (
    <div
      className={`time_popup time_popup_${theme.isDarkMode ? "dark" : "light"}`}
      style={{
        left: `${timeInputPopup.left}px`,
        top: `${timeInputPopup.top + 100}px`,
        visibility: isOpen ? "visible" : "hidden",
      }}>
      <div className="time_popup_col_cont">
        <div className="time_popup_col" ref={hourListRef}>
          {Array.from({ length: maxH - minH + 1 }, (_, i) => i + minH).map(
            i => (
              <TimeInputPopupButton
                key={`hours-${i}`}
                val={i}
                onClick={setHour}
                selected={hour}
              />
            ),
          )}
        </div>
        <div className="time_popup_col" ref={minuteListRef}>
          {Array.from({ length: 60 }, (_, i) => i).map(i => (
            <TimeInputPopupButton
              key={`minutes-${i}`}
              val={i}
              onClick={setMinute}
              selected={minute}
            />
          ))}
        </div>
      </div>

      <Button
        variant="outline"
        style={{
          height: 30,
          width: 60,
          marginTop: 8,
          padding: "0px 5px 0px 5px",
          fontSize: 13,
        }}
        onClick={handleNowClick}>
        Now
      </Button>
    </div>
  );
};
export default TimeInputPopup;
